/**
 * Channel stats — followers, growth and reach across the six owned channels.
 *
 * All six are always drawn, in `CHANNELS` order, whether connected or not. A
 * channel with no collector yet is a card that says which one it is and what it
 * is waiting for; leaving it out would make "we have no LinkedIn numbers" and
 * "we have no LinkedIn" look identical.
 *
 * Everything here is read. The numbers are written by a scheduled Cloud
 * Function, because a platform API called from the browser would ship its token
 * to anyone who opened the page.
 */
import { useEffect, useState } from 'react'
import { nf } from '@/shared/util/numbers'
import {
  subscribeToChannelStats,
  type ChannelStatsMap,
} from './repository/channelStatsRepository'
import { STALE_AFTER_MS } from '@/types/channelStats'
import { CHANNELS, CHANNEL_COLORS, CHANNEL_URLS, SKIDA_WEBSITES, type Channel } from '@/types/posts'
import { formatDate } from '@/shared/util/dates'
import { Mentions } from './Mentions'
import { Trend } from './Trend'
import './ChannelStats.css'

/**
 * What each channel is waiting on, for the cards with no data.
 *
 * Written per channel rather than as one generic line because the answers are
 * genuinely different, and "what do we need to do to get this number" is the
 * only useful thing an empty card can say.
 */
const WAITING_ON: Record<Channel, string> = {
  Instagram: 'Meta app, system user token, and a Facebook Page linked to the Instagram Professional account.',
  Facebook: 'Same Meta app and token as Instagram — one integration covers both.',
  TikTok: 'A TikTok developer app, approved for the stats scopes.',
  YouTube: 'A Google API key restricted to the YouTube Data API.',
  Website: 'A GA4 property with Viewer access for the collector service account.',
  LinkedIn: 'LinkedIn Marketing Developer Platform access for the organisation page.',
  Threads: 'Its own API at graph.threads.net with its own scopes — the threads_* metrics Meta lists on the Instagram node are refused there.',
}

/**
 * What each channel's views figure actually measures, from COPY.md.
 *
 * They are not the same quantity, and the design stopped short of letting them
 * share a label for that reason: Instagram counts post views, Facebook counts
 * visits to the Page profile because Meta retired page-level post reach in the
 * June 2026 deprecation, and the website counts page views in GA4. Three
 * numbers in identical slots invite a comparison none of them supports.
 */
const VIEWS: Partial<Record<Channel, { label: string; caveat?: string }>> = {
  Instagram: { label: 'Views' },
  TikTok: { label: 'Views' },
  YouTube: { label: 'Views' },
  LinkedIn: { label: 'Views' },
  Threads: { label: 'Views' },
  Facebook: {
    label: 'Page visits',
    caveat:
      "Page visits, not post reach — Meta retired that metric; not comparable to Instagram's views.",
  },
  Website: { label: 'Page views' },
}


export function ChannelStats() {
  const [stats, setStats] = useState<ChannelStatsMap>({})
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)

  useEffect(
    () =>
      subscribeToChannelStats(
        (s) => {
          setStats(s)
          setLoading(false)
        },
        (e) => {
          setError(e.message)
          setLoading(false)
        },
      ),
    [],
  )

  const connected = CHANNELS.filter((c) => stats[c]).length

  return (
    <main className="chstats">
      <div className="chstats__head">
        <h2 className="chstats__title">Channel stats</h2>
        <p className="chstats__sub">
          Followers, growth and reach across our own channels.{' '}
          {loading
            ? 'Loading…'
            : connected === 0
              ? 'Nothing connected yet — each card says what it is waiting on.'
              : `${connected} of ${CHANNELS.length} channels connected.`}
        </p>
      </div>

      {error && <div className="empty-note">Could not read channel stats: {error}</div>}

      <div className="chstats__grid">
        {CHANNELS.map((c) => {
          const s = stats[c]
          const stale =
            s?.fetchedAt != null && Date.now() - new Date(s.fetchedAt).getTime() > STALE_AFTER_MS

          return (
            <section className={`chstats__card${s ? '' : ' chstats__card--off'}`} key={c}>
              <div className="chstats__name">
                <span className="chstats__dot" style={{ background: CHANNEL_COLORS[c] }} />
                {c}
              </div>

              {!s ? (
                <>
                  <div className="chstats__notyet">Not connected</div>
                  <p className="chstats__waiting">{WAITING_ON[c]}</p>
                  {/* The account exists even where the numbers do not. */}
                  {CHANNEL_URLS[c] && (
                    <a
                      className="chstats__link"
                      href={CHANNEL_URLS[c]}
                      target="_blank"
                      rel="noreferrer"
                    >
                      View profile ↗
                    </a>
                  )}
                </>
              ) : (
                <>
                  <div className="chstats__followers">
                    {s.followers !== null
                      ? nf(s.followers)
                      : s.views30d !== null
                        ? nf(s.views30d)
                        : '—'}
                  </div>
                  <div className="chstats__change">
                    {/* A website has no followers, so views carry the headline
                        there and the line below says which number you are
                        looking at. Everywhere else it is the follower delta. */}
                    {s.followers === null ? (
                      <span className="muted">{VIEWS[c]?.label ?? 'Views'}, last 30 days</span>
                    ) : s.followersChange30d === null ? (
                      <span className="muted">no 30-day history yet</span>
                    ) : (
                      <span
                        className={
                          s.followersChange30d >= 0 ? 'chstats__up' : 'chstats__down'
                        }
                      >
                        {s.followersChange30d >= 0 ? '+' : '−'}
                        {nf(Math.abs(s.followersChange30d))} in 30 days
                      </span>
                    )}
                  </div>
                  {s.followers !== null && (
                    <>
                      <div className="chstats__reach muted">
                        {VIEWS[c]?.label ?? 'Views'}:{' '}
                        {s.views30d === null ? '—' : nf(s.views30d)} (30d) ·{' '}
                        {s.posts30d === null ? '—' : nf(s.posts30d)} posts
                      </div>
                      {VIEWS[c]?.caveat && (
                        <div className="chstats__caveat">{VIEWS[c]!.caveat}</div>
                      )}
                    </>
                  )}

                  {/* Only where a follower count exists. A website's page views
                      are already a rate, and a sparkline of them would be a
                      second chart of the same thing. */}
                  {s.followers !== null && <Trend samples={s.samples} />}

                  {c === 'Website' && (
                    <div className="chstats__sites">
                      {SKIDA_WEBSITES.map((w) => (
                        <a key={w} href={w} target="_blank" rel="noreferrer">
                          {w.replace(/^https?:\/\//, '')}
                        </a>
                      ))}
                    </div>
                  )}

                  {s.profileUrl && (
                    <a
                      className="chstats__link"
                      href={s.profileUrl}
                      target="_blank"
                      rel="noreferrer"
                    >
                      View profile ↗
                    </a>
                  )}

                  {/* The last two states are the ones that matter most: numbers
                      that stopped updating look exactly like numbers that
                      stopped moving. */}
                  {s.error && <div className="chstats__err">Last fetch failed: {s.error}</div>}
                  {stale && !s.error && (
                    <div className="chstats__err">
                      Not refreshed since {formatDate(s.fetchedAt)} — the collector has missed a
                      run.
                    </div>
                  )}
                </>
              )}
            </section>
          )
        })}
      </div>

      {/* Below the channel cards, because it answers a different question about
          the same connection: not how we are doing, but who is talking. */}
      <Mentions />
    </main>
  )
}
